/*
 * <twc-wotm-info> — Whisky of the Month block (in-window boards).
 * Figma: block/whisky-of-the-month-info + 3 modals (opt-out feedback,
 * delay delivery, request additional bottles).
 *
 * Data: TwcMemberData (shared store). Mutations:
 *   double-up toggle  -> {action:'set_wotm_qty', qty: base|2*base}
 *   qty modal confirm -> {action:'set_wotm_qty', qty: 2*base..12}
 *   opt-out toggle on -> {action:'optout'} then feedback modal
 *     modal submit    -> {action:'optout_feedback', reason, feedback?}
 *     modal add-in    -> {action:'addin', catalogueItemId, qty:1}
 *   opt-out toggle off-> {action:'optin'}
 *   delay modal       -> {action:'set_delay', weeks: 0..3}
 *
 * PAUSED MEMBERS: every box mutation above runs through
 * _mutateBox, which asks a paused member to confirm the resume first and then
 * re-issues the SAME body with `confirmResume: true` — the engine unpauses and
 * applies it in one call (409 PAUSED_CONFIRM_REQUIRED without the flag, which
 * opens the same dialog). _mutateBox's promise settles only once that flow
 * finishes, so callers that chain on success (opt-out -> feedback modal) keep
 * working unchanged.
 *
 * Copy: defaults below; overridden by JSON emitted from the section
 * ([data-twc-wotm-config]) and the four modal snippets
 * (#twc-modal-optout-copy, #twc-modal-qty-copy, #twc-modal-delay-copy,
 * #twc-modal-resume-copy) so storefront locales/settings win. Harness (no
 * Liquid) uses defaults. Modal markup is built here (single source) —
 * snippets carry copy only.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-wotm-info')) return;

  var DEFAULTS = {
    eyebrow: 'Whisky of the Month',
    view_details: 'View product details',
    double_up_label: 'Request Double Up',
    more_bottles_link: 'Request more bottles?',
    optout_label: 'Opt out this month',
    delay_link: 'Delay delivery',
    toggle_yes: 'Yes',
    toggle_no: 'No',
    close: 'Close',
    cancel: 'Cancel',
    optout_title: "You've opted out",
    share_feedback: 'Share feedback',
    feedback_intro: "Our members experience is very important to us - we'd value any feedback on the whisky of the month and The Whisky Club.",
    reason_placeholder: 'Is there a reason you opted out?',
    reason_too_many: 'Too many bottles',
    reason_break: 'Trying a break',
    reason_other: 'Other',
    feedback_placeholder: 'Please share any feedback you have',
    leave_feedback: 'Leave feedback',
    feedback_thanks: 'Thanks — feedback received.',
    fancy_different: 'Fancy something different?',
    addin_cta: 'Add-In',
    more_alternatives: 'More alternatives are waiting in your add-ins below',
    delay_title: 'Delay delivery?',
    delay_note: 'You can delay your delivery by up to 3 weeks. You will still be charged on the Whisky of the Month Monday.',
    delay_none: 'No',
    delay_one: '1 week',
    delay_two: '2 weeks',
    delay_three: '3 weeks',
    delay_confirm: 'Delay delivery',
    qty_title: 'Request Additional Bottles',
    qty_question: 'How many do you require?',
    qty_placeholder: 'Enter quantity',
    confirm: 'Confirm',
    window_closed: 'The edit window has closed — refreshing your box.',
    fixture_mode: 'Preview mode — actions are disabled.',
    action_failed: "That didn't work — please try again.",
    optout_failed: 'We were unable to opt you out due to a technical error. Please refresh the page and try again or contact our customer care team.',
    // Resume-confirm modal (snippets/twc-modal-resume.liquid). Prefixed on
    // read so the shared blob's generic `confirm` / `cancel` / `close` keys
    // cannot overwrite the qty and delay modals' own labels.
    resume_title: 'Resume your membership?',
    resume_body: "Your membership is paused. Carrying on resumes it now, so you're back to receiving your Whisky of the Month each month. If this month's selection window has already closed you'll rejoin from next month's bottle. Anything you add today is charged as normal — top-up orders are processed at 11:30pm tonight.",
    resume_confirm: 'Resume & continue',
    resume_cancel: 'Cancel',
    resume_failed: "We couldn't resume your membership. Please try again.",
    resume_working: 'Working…',
  };

  function readJson(el) {
    if (!el) return null;
    try { return global.TwcAccountApi.decodeDeep(JSON.parse(el.textContent)); } catch (e) { return null; }
  }

  // The resume blob is shared with the grids, so its keys are generic
  // (title/body/confirm/…). Namespace them into this island's flat copy bag.
  function prefixed(blob, prefix) {
    var out = {};
    if (!blob) return out;
    Object.keys(blob).forEach(function (k) { out[prefix + k] = blob[k]; });
    return out;
  }

  function sizedImageUrl(url, width) {
    if (!url) return null;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // An engine message reaches a member ONLY when its code is one the engine
  // writes in member language. Everything else (INTERNAL_ERROR, NOT_FOUND,
  // transport lines carrying raw `box sub_…/camp_…` ids) falls back to this
  // island's local copy. WINDOW_CLOSED and FIXTURE_MODE keep their dedicated
  // branches at the call sites. Same pattern as twc-box-card.js.
  var MEMBER_SAFE_CODES = {
    CONFLICT: true,
    // A member with no payment method: box, Top Up and add-in writes are
    // refused with the engine's member-facing reason.
    NO_PAYMENT_METHOD: true,
    PAUSED_CONFIRM_REQUIRED: true,
    UNSUBSCRIBED_CONFIRM_REQUIRED: true,
    VALIDATION_ERROR: true,
  };

  function memberMessage(r, fallback) {
    return (r && MEMBER_SAFE_CODES[r.code] === true && r.message) || fallback;
  }

  function whenDeps(cb) {
    if (global.TwcMemberData && global.TwcAccountApi) { cb(); return; }
    var tries = 0;
    var t = setInterval(function () {
      if (global.TwcMemberData && global.TwcAccountApi) { clearInterval(t); cb(); }
      else if (++tries > 200) { clearInterval(t); }
    }, 25);
  }

  var TwcWotmInfo = function () {
    return Reflect.construct(HTMLElement, [], TwcWotmInfo);
  };
  TwcWotmInfo.prototype = Object.create(HTMLElement.prototype);
  TwcWotmInfo.prototype.constructor = TwcWotmInfo;

  TwcWotmInfo.prototype.connectedCallback = function () {
    var self = this;
    this._ac = new AbortController();
    this._pending = false;
    this._state = null;
    // The section markup carries this class; harness mounts the bare tag.
    this.classList.add('twc-wotm');

    var cfg = readJson(this.querySelector('[data-twc-wotm-config]')) || {};
    var optoutCopy = readJson(document.getElementById('twc-modal-optout-copy')) || {};
    var qtyCopy = readJson(document.getElementById('twc-modal-qty-copy')) || {};
    var delayCopy = readJson(document.getElementById('twc-modal-delay-copy')) || {};
    var resumeCopy = prefixed(readJson(document.getElementById('twc-modal-resume-copy')), 'resume_');
    this.copy = Object.assign({}, DEFAULTS, cfg.copy || {}, optoutCopy, qtyCopy, delayCopy, resumeCopy);
    this.productCfg = cfg.product || null; // {title, url, image, descriptionHtml}
    this.campaignCfg = cfg.campaign || null; // {title, url, image} from metaobjects.campaign['current']
    this.badgeUrl = cfg.badge || null;
    // Liquid resolves the exclusivity metafields (island JSON never carries
    // them); null = not exclusive, string = the ready-translated line.
    this.exclusiveLine = cfg.exclusiveLine || null;

    // The section paints a Liquid instant layer (real bottle, real image) when
    // the campaign metaobject says we are in window. Never trade that for a
    // shimmer: render() replaces this element's innerHTML wholesale, so the
    // instant layer is removed by the same write that draws the live panel.
    if (!this.querySelector('[data-twc-wotm-instant]')) {
      this.innerHTML = '<div class="twc-wotm__panel twc-skeleton" style="min-height:180px"></div>';
    }

    // One delegated listener per event type; AbortController cleans up in
    // disconnectedCallback (removeEventListener equivalent).
    this.addEventListener('click', function (e) { self._onClick(e); }, { signal: this._ac.signal });
    this.addEventListener('submit', function (e) { self._onSubmit(e); }, { signal: this._ac.signal });

    whenDeps(function () {
      self._unsub = global.TwcMemberData.subscribe(function (s) {
        self._state = s;
        self.render(s);
      });
    });
  };

  TwcWotmInfo.prototype.disconnectedCallback = function () {
    if (this._unsub) { this._unsub(); this._unsub = null; }
    if (this._ac) this._ac.abort();
    this._closeDialog(true);
  };

  // ---- derived helpers ---------------------------------------------------

  TwcWotmInfo.prototype._base = function () {
    var me = this._state && this._state.me;
    return me && me.tier === 'two_bottles' ? 2 : 1;
  };

  TwcWotmInfo.prototype._featuredLine = function () {
    var box = this._state && this._state.box;
    if (!box || !box.lines) return null;
    return box.lines.find(function (l) { return l.source === 'base' && l.role === 'featured'; }) || null;
  };

  // Board/live media seam: the campaign's featured product may carry a
  // mediaAsset (bare theme-asset filename) resolved against the CDN base any
  // account section publishes. Merchant product picker always wins.
  TwcWotmInfo.prototype._featuredMedia = function (s, line) {
    if (!global.__TWC_ASSET_BASE) return null;
    var products = s.campaignProducts || [];
    var featured = null;
    for (var i = 0; i < products.length; i++) {
      if (products[i].role !== 'featured') continue;
      if (line && products[i].catalogueItemId === line.catalogueItemId) { featured = products[i]; break; }
      if (!featured) featured = products[i];
    }
    return featured && featured.mediaAsset ? global.__TWC_ASSET_BASE + featured.mediaAsset : null;
  };

  // ---- rendering ---------------------------------------------------------

  TwcWotmInfo.prototype.render = function (s) {
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;

    if (!s.loaded) return;

    var show = s.cycleState === 'in_window' && s.box && s.box.box;
    this.hidden = !show;
    var host = this.closest('.shopify-section');
    if (host) host.classList.toggle('twc-hidden', !show);
    if (!show) return;

    var base = this._base();
    var line = this._featuredLine();
    var isOptout = !!s.box.box.isOptout;
    var isDoubleup = !!s.box.box.isDoubleup;
    var qty = line ? line.qty : base;

    // Campaign metaobject is the last fallback on all three: without it, a
    // store with no merchant-picked product would paint the Liquid instant
    // layer's bottle and then drop it to a placeholder when the island took
    // over. Merchant pick still wins, then engine truth, then the campaign.
    var camp = this.campaignCfg || {};
    var title = (this.productCfg && this.productCfg.title) || (line && line.title) || camp.title || '';
    var url = (this.productCfg && this.productCfg.url) || camp.url || '';
    var img = (this.productCfg && this.productCfg.image) || this._featuredMedia(s, line) || camp.image || null;
    var desc = (this.productCfg && this.productCfg.descriptionHtml) || '';
    var badge = this.badgeUrl;

    var html =
      '<div class="twc-wotm__panel">' +
        '<div class="twc-wotm__content">' +
          '<p class="twc-wotm__eyebrow">' + esc(c.eyebrow) + '</p>' +
          '<h3 class="twc-wotm__title">' + esc(title) + '</h3>' +
          (this.exclusiveLine ? '<p class="twc-wotm__exclusive">' + esc(this.exclusiveLine) + '</p>' : '') +
          (desc ? '<div class="twc-wotm__desc">' + desc + '</div>' : '') +
          (url ? '<p class="twc-wotm__view"><a href="' + esc(url) + '">' + esc(c.view_details) + '</a></p>' : '') +
          '<div class="twc-wotm__toggles">' +
            this._toggleRow('doubleup', c.double_up_label, c.more_bottles_link, 'qty-modal', isDoubleup) +
            this._toggleRow('optout', c.optout_label, c.delay_link, 'delay-modal', isOptout) +
          '</div>' +
        '</div>' +
        '<div class="twc-wotm__media">' +
          (img ? '<img src="' + esc(img) + '" alt="' + esc(title) + '" loading="lazy">' : '<div class="twc-wotm__media-placeholder" aria-hidden="true"></div>') +
          (img && badge ? '<span class="twc-wotm__stamp"><img src="' + esc(badge) + '" alt="" loading="lazy"></span>' : '') +
        '</div>' +
      '</div>';

    this.innerHTML = html;
    if (qty > base && !isDoubleup) {
      // qty was raised via the modal without the toggle flag — reflect it
      var sw = this.querySelector('[data-twc-toggle="doubleup"]');
      if (sw) { sw.setAttribute('aria-checked', 'true'); var t = sw.querySelector('.twc-switch__text'); if (t) t.textContent = this.copy.toggle_yes; }
    }
  };

  TwcWotmInfo.prototype._toggleRow = function (key, label, linkText, linkAct, on) {
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;
    return (
      '<div class="twc-wotm__toggle-row">' +
        '<div class="twc-wotm__toggle-copy">' +
          '<p class="twc-wotm__toggle-label">' + esc(label) + '</p>' +
          '<button type="button" class="twc-btn--link twc-wotm__toggle-link" data-twc-act="' + linkAct + '">' + esc(linkText) + '</button>' +
        '</div>' +
        '<button type="button" role="switch" aria-checked="' + (on ? 'true' : 'false') + '" aria-label="' + esc(label) + '"' +
          ' class="twc-switch" data-twc-toggle="' + key + '"' + (this._pending ? ' aria-disabled="true"' : '') + '>' +
          '<span class="twc-switch__text">' + esc(on ? c.toggle_yes : c.toggle_no) + '</span>' +
          '<span class="twc-switch__knob" aria-hidden="true"></span>' +
        '</button>' +
      '</div>'
    );
  };

  // ---- events -------------------------------------------------------------

  TwcWotmInfo.prototype._onClick = function (e) {
    var t = e.target.closest('[data-twc-toggle], [data-twc-act], [data-twc-close]');
    if (!t || this._pending) {
      if (t && t.hasAttribute('data-twc-close')) this._closeDialog();
      return;
    }
    if (t.hasAttribute('data-twc-close')) { this._closeDialog(); return; }

    var toggle = t.getAttribute('data-twc-toggle');
    if (toggle === 'doubleup') return this._toggleDoubleup(t);
    if (toggle === 'optout') return this._toggleOptout(t);

    var act = t.getAttribute('data-twc-act');
    if (act === 'qty-modal') return this._openQtyModal();
    if (act === 'delay-modal') return this._openDelayModal();
    if (act === 'optout-addin') return this._optoutAddin(t);
    if (act === 'resume-confirm') return this._confirmResume(t);
  };

  TwcWotmInfo.prototype._onSubmit = function (e) {
    var form = e.target.closest('[data-twc-form]');
    if (!form) return;
    e.preventDefault();
    var kind = form.getAttribute('data-twc-form');
    if (kind === 'optout-feedback') this._submitFeedback(form);
    if (kind === 'delay') this._confirmDelay();
    if (kind === 'qty') this._confirmQty();
  };

  // ---- mutations ----------------------------------------------------------

  // Paused member acting on a box control: ask first, then re-issue the same
  // body with confirmResume:true. The returned promise settles only when the
  // whole flow has (mutation applied, or the member cancelled), so callers that
  // chain on r.ok — the opt-out toggle opening its feedback modal, the qty
  // modal closing itself — need no knowledge of any of this.
  TwcWotmInfo.prototype._isPaused = function () {
    var s = this._state || (global.TwcMemberData && global.TwcMemberData.getState());
    return !!(s && s.me && s.me.status === 'paused');
  };

  TwcWotmInfo.prototype._mutateBox = function (body, confirmResume) {
    var self = this;
    var c = this.copy;
    if (!confirmResume && this._isPaused()) {
      return this._openResumeDialog(function () { return self._mutateBox(body, true); });
    }
    var payload = confirmResume ? Object.assign({}, body, { confirmResume: true }) : body;
    // Opting out has its own failure copy; every other box action shares one.
    var failedCopy = body.action === 'optout' ? (c.optout_failed || c.action_failed) : c.action_failed;
    this._pending = true;
    return global.TwcMemberData.mutate('/box/current', payload).then(function (r) {
      self._pending = false;
      // The pause landed between page load and this click: same dialog, from
      // the engine's answer rather than from cached state.
      if (!r.ok && r.code === 'PAUSED_CONFIRM_REQUIRED') {
        if (self._state && self.isConnected) self.render(self._state);
        return self._openResumeDialog(function () { return self._mutateBox(body, true); });
      }
      // The CANCEL landed between page load and this click. No rejoin dialog
      // inside a panel an unsubscribed member can never see: one composed
      // refresh flips the dashboard to the unsubscribed presentation and this
      // panel hides itself (show is in_window only).
      if (!r.ok && r.code === 'UNSUBSCRIBED_CONFIRM_REQUIRED') {
        return global.TwcMemberData.refresh().then(function () { return r; });
      }
      if (!r.ok) {
        if (r.code === 'WINDOW_CLOSED') {
          global.TwcAccountApi.flash(c.window_closed, 'warn');
          global.TwcMemberData.refresh();
        } else if (r.code === 'FIXTURE_MODE') {
          global.TwcAccountApi.flash(c.fixture_mode, 'info');
        } else {
          global.TwcAccountApi.flash(memberMessage(r, failedCopy), 'error');
        }
      } else {
        global.TwcAccountApi.flash('', '');
      }
      // On success the store broadcast the fresh box BEFORE this handler ran
      // (mutate's own .then applies it), so the subscribe re-render happened
      // while _pending was still true — leaving both switches stuck with
      // aria-disabled="true" (CSS: pointer-events none), which makes opt-out
      // look terminal and blocks double-up re-toggling. Re-render now that
      // the mutation has settled so the toggles come back to life.
      if (self._state && self.isConnected) self.render(self._state);
      // A successful box action lands the member on the Delivery Box card,
      // where the change just repainted (the store
      // broadcast above happened synchronously inside mutate's .then). Two
      // exceptions, both opt-out: `optout` scrolls only after its feedback
      // modal closes (the one-shot close listener in _toggleOptout — never
      // behind the open dialog), and `optout_feedback` is that modal's own
      // submit, covered by the same close listener.
      if (r.ok && body.action !== 'optout' && body.action !== 'optout_feedback') {
        global.TwcMemberData.scrollToBox();
      }
      // A resumed member is ACTIVE now, but this response carried the box, not
      // `me` — the store still says paused. One composed GET (the refresh the
      // doctrine reserves for identity actions) realigns every island.
      if (r.ok && confirmResume) return global.TwcMemberData.refresh().then(function () { return r; });
      return r;
    });
  };

  TwcWotmInfo.prototype._toggleDoubleup = function () {
    var s = this._state;
    if (!s || !s.box) return;
    var base = this._base();
    var line = this._featuredLine();
    var isUp = s.box.box.isDoubleup || (line ? line.qty > base : false);
    this._mutateBox({ action: 'set_wotm_qty', qty: isUp ? base : 2 * base });
  };

  TwcWotmInfo.prototype._toggleOptout = function () {
    var self = this;
    var s = this._state;
    if (!s || !s.box) return;
    if (s.box.box.isOptout) {
      this._mutateBox({ action: 'optin' });
    } else {
      this._mutateBox({ action: 'optout' }).then(function (r) {
        if (!r.ok) return;
        self._openOptoutModal();
        // Opt-out scrolls to the box only once its feedback modal is GONE —
        // the member dismisses it, or the
        // thanks panel auto-closes it — never behind the open dialog. Every
        // close path funnels through _closeDialog's dialog.close(), which
        // fires the <dialog> `close` event; {once} keeps the scroll
        // one-shot, and the island's AbortController drops the listener if
        // the island unmounts first (disconnectedCallback aborts BEFORE it
        // force-closes the dialog).
        if (self._dialog) {
          self._dialog.addEventListener('close', function () {
            global.TwcMemberData.scrollToBox();
          }, { once: true, signal: self._ac.signal });
        }
      });
    }
  };

  TwcWotmInfo.prototype._submitFeedback = function (form) {
    var self = this;
    var c = this.copy;
    var reason = form.querySelector('[name="reason"]').value;
    var feedback = form.querySelector('[name="feedback"]').value.trim();
    if (!reason) { form.querySelector('[name="reason"]').focus(); return; }
    var body = { action: 'optout_feedback', reason: reason };
    if (feedback) body.feedback = feedback;
    this._mutateBox(body).then(function (r) {
      if (!r.ok) return;
      var panel = form.parentElement;
      panel.innerHTML = '<p class="twc-wotm-modal__thanks">' + global.TwcAccountApi.escapeHtml(c.feedback_thanks) + '</p>';
      setTimeout(function () { self._closeDialog(); }, 1500);
    });
  };

  TwcWotmInfo.prototype._optoutAddin = function (btn) {
    var self = this;
    var id = btn.getAttribute('data-catalogue-item');
    if (!id) return;
    this._mutateBox({ action: 'addin', catalogueItemId: id, qty: 1 }).then(function (r) {
      if (r.ok) self._closeDialog();
    });
  };

  // The delay is a BOX mutation, not a standalone delivery preference. It
  // goes through _mutateBox like every other box action, so it inherits the
  // paused-member resume confirmation, the WINDOW_CLOSED refusal, the 409
  // message the engine words itself, and the scroll to the card. No refresh:
  // the response IS the fresh box, and the
  // store applies it before this handler runs, so the card the member lands on
  // already shows the new delivery date. _confirmQty has the same shape.
  TwcWotmInfo.prototype._confirmDelay = function () {
    var self = this;
    var sel = this._dialog && this._dialog.querySelector('[name="delayWeeks"]');
    if (!sel) return;
    var weeks = parseInt(sel.value, 10) || 0;
    this._mutateBox({ action: 'set_delay', weeks: weeks }).then(function (r) {
      if (r.ok) self._closeDialog();
    });
  };

  TwcWotmInfo.prototype._confirmQty = function () {
    var self = this;
    var input = this._dialog && this._dialog.querySelector('[name="qty"]');
    if (!input) return;
    var base = this._base();
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 2 * base || qty > 12) { input.focus(); input.setAttribute('aria-invalid', 'true'); return; }
    this._mutateBox({ action: 'set_wotm_qty', qty: qty }).then(function (r) {
      if (r.ok) self._closeDialog();
    });
  };

  // ---- dialogs ------------------------------------------------------------

  TwcWotmInfo.prototype._openDialog = function (id, titleHtml, bodyHtml, opts) {
    var self = this;
    var c = this.copy;
    this._closeDialog(true);
    var dlg = document.createElement('dialog');
    dlg.className = 'twc-dialog twc-wotm-dialog' + (opts && opts.wide ? ' twc-wotm-dialog--wide' : '');
    dlg.setAttribute('aria-labelledby', id + '-title');
    dlg.innerHTML =
      '<div class="twc-dialog__head">' +
        '<h3 class="twc-dialog__title" id="' + id + '-title">' + titleHtml + '</h3>' +
        '<button type="button" class="twc-dialog__close" data-twc-close aria-label="' + global.TwcAccountApi.escapeHtml(c.close) + '">&times;</button>' +
      '</div>' +
      bodyHtml;
    // Mounted on <body>, NOT inside the island: island re-renders (every box
    // broadcast replaces innerHTML) must not destroy an open dialog. The
    // dialog therefore gets its own delegated listeners, cleaned up by
    // element removal + the island's AbortController signal.
    document.body.appendChild(dlg);
    this._dialog = dlg;
    this._opener = document.activeElement;
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) { self._closeDialog(); return; } // backdrop
      self._onClick(e);
    }, { signal: this._ac.signal });
    dlg.addEventListener('submit', function (e) { self._onSubmit(e); }, { signal: this._ac.signal });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); self._closeDialog(); }, { signal: this._ac.signal });
    dlg.showModal();
    document.documentElement.style.overflow = 'hidden';
  };

  TwcWotmInfo.prototype._closeDialog = function (silent) {
    if (!this._dialog) return;
    try { this._dialog.close(); } catch (e) { /* already closed */ }
    this._dialog.remove();
    this._dialog = null;
    document.documentElement.style.overflow = '';
    if (!silent && this._opener && this._opener.isConnected) {
      try { this._opener.focus(); } catch (e) { /* ignore */ }
    }
    this._opener = null;
    // Closing an unanswered resume dialog IS the member's answer: no.
    this._settleResume({ ok: false, code: 'RESUME_CANCELLED', message: null, data: null });
  };

  // ---- resume-confirm dialog ---------------------------------------------

  // Resolves the promise _openResumeDialog handed its caller, exactly once.
  TwcWotmInfo.prototype._settleResume = function (result) {
    var resolve = this._resumeResolve;
    this._resumeResolve = null;
    if (resolve) resolve(result);
  };

  TwcWotmInfo.prototype._dialogError = function (message) {
    var line = this._dialog && this._dialog.querySelector('[data-twc-dialog-error]');
    if (!line) return;
    line.textContent = message || '';
    line.hidden = !message;
  };

  // run() re-issues the caller's mutation with confirmResume:true. Returns a
  // promise that settles with that mutation's result, or with RESUME_CANCELLED
  // if the member backs out.
  TwcWotmInfo.prototype._openResumeDialog = function (run) {
    var self = this;
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;
    var settle;
    var promise = new Promise(function (resolve) { settle = resolve; });
    // _openDialog closes whatever was open, and closing settles an unanswered
    // resume promise — so this one is registered only once that has happened.
    this._openDialog(
      'twc-resume-modal',
      esc(c.resume_title),
      '<div class="twc-dialog__body">' +
        '<p class="twc-dialog__sub">' + esc(c.resume_body) + '</p>' +
      '</div>' +
      '<p class="twc-dialog__error" data-twc-dialog-error role="alert" hidden></p>' +
      '<div class="twc-dialog__foot">' +
        '<button type="button" class="twc-mbtn twc-mbtn--ghost" data-twc-close>' + esc(c.resume_cancel) + '</button>' +
        '<button type="button" class="twc-mbtn" data-twc-act="resume-confirm">' + esc(c.resume_confirm) + '</button>' +
      '</div>'
    );
    this._resumeRun = run;
    this._resumeResolve = settle;
    return promise;
  };

  TwcWotmInfo.prototype._confirmResume = function (btn) {
    var self = this;
    var c = this.copy;
    var run = this._resumeRun;
    if (!run || btn.disabled) return;
    this._dialogError('');
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = c.resume_working || 'Working…';
    run().then(function (r) {
      if (r && r.ok) {
        self._settleResume(r);
        self._closeDialog();
        return;
      }
      // Stay open and say why, where the member is looking.
      if (btn.isConnected) {
        btn.disabled = false;
        btn.textContent = label;
      }
      self._dialogError(
        (r && r.code === 'FIXTURE_MODE') ? c.fixture_mode : memberMessage(r, c.resume_failed || c.action_failed)
      );
    });
  };

  TwcWotmInfo.prototype._openOptoutModal = function () {
    var self = this;
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;
    // Contract v3 §7 deterministic offer pick: campaign products filtered
    // eligibility=optout_only AND role=addon, sorted sortOrder asc — the hero
    // is the first. N>1 adds a one-line pointer to the add-ins grid under the
    // hero CTA (no carousel). The engine candidate owns its product identity
    // and cached Shopify image URL; no second merchant product picker can
    // disagree with it.
    var all = (this._state && this._state.campaignProducts) || [];
    var candidates = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].eligibility === 'optout_only' && all[i].role === 'addon') candidates.push(all[i]);
    }
    candidates.sort(function (a, b) { return (a.sortOrder || 0) - (b.sortOrder || 0); });
    var engineSwap = candidates.length ? candidates[0] : null;
    var swap = null;
    if (engineSwap) {
      swap = {
        catalogueItemId: engineSwap.catalogueItemId,
        title: engineSwap.title,
        priceCents: engineSwap.unitPriceCents,
        image: sizedImageUrl(engineSwap.imageUrl, 400),
      };
    }
    var swapHtml = '';
    if (swap && swap.catalogueItemId) {
      swapHtml =
        '<div class="twc-wotm-modal__panel">' +
          '<h4>' + esc(c.fancy_different) + '</h4>' +
          '<div class="twc-wotm-modal__swap">' +
            // The media box always renders (cream placeholder when the engine
            // has no cached image) so the card never collapses — same shape as
            // the qty modal's .twc-wotm-modal__product-thumb.
            '<div class="twc-wotm-modal__swap-media">' +
              (swap.image ? '<img src="' + esc(swap.image) + '" alt="" loading="lazy">' : '') +
            '</div>' +
            '<p class="twc-wotm-modal__swap-title">' + esc(swap.title || '') + '</p>' +
            (swap.priceCents != null && this._state && this._state.me
              ? '<p class="twc-wotm-modal__swap-price">' + global.TwcAccountApi.money(swap.priceCents, this._state.me.currency) + '</p>' : '') +
            '<button type="button" class="twc-mbtn twc-mbtn--block" data-twc-act="optout-addin" data-catalogue-item="' + esc(swap.catalogueItemId) + '">' + esc(c.addin_cta) + '</button>' +
            (candidates.length > 1 ? '<p class="twc-wotm-modal__swap-more">' + esc(c.more_alternatives) + '</p>' : '') +
          '</div>' +
        '</div>';
    }
    // Getsitecontrol opt-out survey: a configured widget id REPLACES the
    // native reason form — one survey per
    // moment, and GSC holds every survey's answers in one place. The blank id
    // keeps the native form and its engine-stored optout_feedback. The blob
    // and the gsc stub come from snippets/twc-gsc-survey.liquid; either one
    // missing means the integration is off. The widget's GSC-side placement
    // must target #twc-gsc-optout-slot.
    var gscCfg = readJson(document.getElementById('twc-gsc-config')) || {};
    var gscId = parseInt(gscCfg.optout_widget_id, 10);
    var useGsc = !!gscId && typeof global.gsc === 'function';
    var feedbackHtml = useGsc
      ? '<div class="twc-wotm-modal__panel" id="twc-gsc-optout-slot"></div>'
      : '<form class="twc-wotm-modal__panel" data-twc-form="optout-feedback">' +
          '<h4>' + esc(c.share_feedback) + '</h4>' +
          '<p class="twc-dialog__sub">' + esc(c.feedback_intro) + '</p>' +
          '<select name="reason" required aria-label="' + esc(c.reason_placeholder) + '">' +
            '<option value="" disabled selected>' + esc(c.reason_placeholder) + '</option>' +
            '<option value="too_many_bottles">' + esc(c.reason_too_many) + '</option>' +
            '<option value="trying_a_break">' + esc(c.reason_break) + '</option>' +
            '<option value="other">' + esc(c.reason_other) + '</option>' +
          '</select>' +
          '<textarea name="feedback" rows="3" placeholder="' + esc(c.feedback_placeholder) + '"></textarea>' +
          '<button type="submit" class="twc-mbtn twc-mbtn--block">' + esc(c.leave_feedback) + '</button>' +
        '</form>';
    this._openDialog(
      'twc-optout-modal',
      esc(c.optout_title),
      '<div class="twc-wotm-modal__cols' + (swapHtml ? ' has-swap' : '') + '">' +
        feedbackHtml +
        swapHtml +
      '</div>',
      { wide: !!swapHtml }
    );
    if (useGsc) {
      if (!this._gscHooked) {
        this._gscHooked = true;
        // The callback fires for EVERY widget, so the id filter is not
        // optional. Closing here mirrors the native form's thanks-then-close;
        // the dialog's own close event still scrolls to the box (_toggleOptout).
        global.gsc('onSubmit', function (widgetId) {
          if (parseInt(widgetId, 10) === gscId && self._dialog) self._closeDialog();
        });
      }
      if (gscCfg.email_hash) global.gsc('params', { email_address_hash: gscCfg.email_hash });
      global.gsc('show', gscId);
    }
  };

  TwcWotmInfo.prototype._openDelayModal = function () {
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;
    // The BOX carries the delay, and a box mutation applies in place without
    // a composed refetch — so the box slice is the fresh value and `prefs`
    // can be a page-load old. A payload with no `delivery` (no campaign)
    // reads as no delay.
    var s = this._state;
    var current =
      (s && s.box && s.box.delivery && s.box.delivery.delayWeeks) || 0;
    var labels = [c.delay_none, c.delay_one, c.delay_two, c.delay_three];
    var opts = labels.map(function (label, i) {
      return '<option value="' + i + '"' + (i === current ? ' selected' : '') + '>' + esc(label) + '</option>';
    }).join('');
    this._openDialog(
      'twc-delay-modal',
      esc(c.delay_title),
      '<form data-twc-form="delay">' +
        '<div class="twc-dialog__body">' +
          '<p class="twc-dialog__sub">' + esc(c.delay_note) + '</p>' +
          '<select name="delayWeeks" aria-label="' + esc(c.delay_title) + '">' + opts + '</select>' +
        '</div>' +
        '<div class="twc-dialog__foot">' +
          '<button type="button" class="twc-mbtn twc-mbtn--ghost" data-twc-close>' + esc(c.cancel) + '</button>' +
          '<button type="submit" class="twc-mbtn">' + esc(c.delay_confirm) + '</button>' +
        '</div>' +
      '</form>'
    );
  };

  TwcWotmInfo.prototype._openQtyModal = function () {
    var c = this.copy;
    var esc = global.TwcAccountApi.escapeHtml;
    var base = this._base();
    var line = this._featuredLine();
    var min = 2 * base;
    var value = line && line.qty >= min ? line.qty : min;
    var title = (this.productCfg && this.productCfg.title) || (line && line.title) || '';
    var img = this.productCfg && this.productCfg.image;
    this._openDialog(
      'twc-qty-modal',
      esc(c.qty_title),
      '<form data-twc-form="qty">' +
        '<div class="twc-dialog__body">' +
          '<div class="twc-wotm-modal__product">' +
            '<div class="twc-wotm-modal__product-thumb">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '') + '</div>' +
            '<div class="twc-wotm-modal__product-copy">' +
              '<p class="twc-wotm-modal__product-eyebrow">' + esc(c.eyebrow) + '</p>' +
              '<p class="twc-wotm-modal__product-title">' + esc(title) + '</p>' +
            '</div>' +
          '</div>' +
          '<div class="twc-dialog__field">' +
            '<label class="twc-dialog__sub" for="twc-qty-input">' + esc(c.qty_question) + '</label>' +
            '<input id="twc-qty-input" name="qty" type="number" inputmode="numeric" min="' + min + '" max="12" step="1" value="' + value + '" placeholder="' + esc(c.qty_placeholder) + '">' +
          '</div>' +
        '</div>' +
        '<div class="twc-dialog__foot">' +
          '<button type="button" class="twc-mbtn twc-mbtn--ghost" data-twc-close>' + esc(c.cancel) + '</button>' +
          '<button type="submit" class="twc-mbtn">' + esc(c.confirm) + '</button>' +
        '</div>' +
      '</form>'
    );
  };

  global.customElements.define('twc-wotm-info', TwcWotmInfo);
})(window);
